import { normaliseGfmAlerts } from '@affine/core/modules/orchestration/hooks';
import type { EditorHost } from '@blocksuite/affine/block-std';
import { GfxControllerIdentifier } from '@blocksuite/affine/block-std/gfx';
import {
  CanvasElementType,
  EdgelessCRUDIdentifier,
  getSurfaceBlock,
} from '@blocksuite/affine/blocks/surface';
import { Bound } from '@blocksuite/affine/global/gfx';
import {
  type Color,
  ConnectorMode,
  DefaultTheme,
  NoteDisplayMode,
} from '@blocksuite/affine/model';
import { TelemetryProvider } from '@blocksuite/affine/shared/services';

import { insertFromMarkdown } from '../../../utils';
import { getAIPanelWidget } from '../../utils/ai-widgets';
import { getEdgelessCopilotWidget } from '../../utils/edgeless';
import type {
  ExploreItem,
  ExploreTree,
  SectionKind,
} from './parse';

// --- Dimensions (canvas pixels) ------------------------------------------
const TLDR_WIDTH = 640;
const TLDR_HEIGHT = 160;
const ROOT_WIDTH = 200;
const ROOT_HEIGHT = 60;
const SECTION_WIDTH = 240;
const SECTION_HEIGHT = 60;
const FINDING_WIDTH = 320;
const FINDING_HEIGHT = 64;
const CITATION_WIDTH = 200;
const CITATION_HEIGHT = 60;

// Gaps
const TLDR_TO_TREE_GAP = 100;
const LEVEL_H_GAP = 100;
// Extra vertical breathing room BETWEEN sibling subtrees so a reader can
// see where one section's group of items ends and the next begins, even
// without connectors.
const SIBLING_V_GAP = 50;
const CITATION_V_GAP = 8;
const RUN_STACK_GAP = 200;

// Text caps
const MAX_NODE_TEXT_ROOT = 40;
const MAX_NODE_TEXT_SECTION = 40;
const MAX_NODE_TEXT_FINDING = 80;

// Per-section item cap — keeps dense AI responses from creating an
// overwhelming wall of cards.
const MAX_ITEMS_PER_SECTION = 5;

// --- Connector styling ---------------------------------------------------
// Explicit values override whatever the EditPropsStore has cached from a
// previous user action (e.g. a transparent stroke), which otherwise wins
// the `mergeWith(lastProps, props)` in `crud.addElement`.
const CONNECTOR_STROKE_WIDTH = 2;
const CONNECTOR_STROKE_STYLE = 'solid';

// --- Per-doc stacking registry ------------------------------------------
const lastRegionByDoc = new WeakMap<object, Bound>();

// Per-doc session registry — tracks the prior turn's root node so each
// follow-up turn can draw a cross-turn connector back to it. Turns the
// stacked-subtrees layout into one visually connected session graph.
const sessionRootByDoc = new WeakMap<
  object,
  { rootId: string; turnCount: number }
>();

// --- Depth-based note backgrounds ---------------------------------------
// Keep this light & readable; the tree is denser than a typical Curiescious
// canvas so heavy colours are distracting.
const NOTE_BG = DefaultTheme.NoteBackgroundColorMap;

// --- Layout types --------------------------------------------------------

type NodeKind = 'root' | 'section' | 'finding' | 'citation';

type LayoutedNode = {
  id: string;
  kind: NodeKind;
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
  subtreeHeight: number;
  children: LayoutedNode[];
  item?: ExploreItem;
  sectionKind?: SectionKind;
  /** For citation nodes: the canonical citation token (PMID:..., DOI:..., ...). */
  citationToken?: string;
};

/**
 * Convert a parsed Explore tree into an edgeless layout made entirely of
 * `affine:note` blocks plus curved/orthogonal connectors. No native
 * mindmap element is involved, so the layout is 100% deterministic.
 *
 * Four columns:
 *   col 0: root
 *   col 1: sections
 *   col 2: findings / items
 *   col 3: citation hubs (aligned per-finding)
 *
 * Findings without citations skip col 3 entirely. Citations cited by
 * multiple findings are placed once (at the first citing finding's row)
 * and get extra connectors drawn in.
 */
export async function buildExploreMindMap(
  host: EditorHost,
  tree: ExploreTree
): Promise<void> {
  const doc = host.doc;
  if (!doc.root) return;

  const surface = getSurfaceBlock(doc);
  if (!surface) return;

  const gfx = host.std.get(GfxControllerIdentifier);
  const crud = host.std.get(EdgelessCRUDIdentifier);
  const edgelessCopilot = getEdgelessCopilotWidget(host) ?? null;
  const aiPanel = (() => {
    try {
      return getAIPanelWidget(host);
    } catch {
      return null;
    }
  })();

  const { originX, originY } = computeInsertionOrigin(
    host,
    surface,
    edgelessCopilot,
    TLDR_WIDTH,
    TLDR_HEIGHT
  );

  // === 1. TL;DR note ====================================================
  let tldrNoteId: string | undefined;
  if (tree.tldrMarkdown) {
    doc.transact(() => {
      tldrNoteId = doc.addBlock(
        'affine:note',
        {
          xywh: new Bound(
            originX,
            originY,
            TLDR_WIDTH,
            TLDR_HEIGHT
          ).serialize(),
          displayMode: NoteDisplayMode.EdgelessOnly,
          background: NOTE_BG.White,
        },
        doc.root!.id
      );
    });
    if (tldrNoteId) {
      await insertFromMarkdown(
        host,
        normaliseGfmAlerts(tree.tldrMarkdown),
        doc,
        tldrNoteId,
        0
      );
    }
  }

  // === 2. Compute tree layout (pure JS, no Yjs mutations yet) ===========
  const treeX = originX;
  const treeY = originY + (tldrNoteId ? TLDR_HEIGHT + TLDR_TO_TREE_GAP : 0);
  const layout = computeTreeLayout(tree, treeX, treeY);

  // Collected node metadata for later steps.
  const findingNoteByKey = new Map<string, string>();
  const citationNoteByToken = new Map<string, string>();
  const treeNotePositions: Bound[] = [];
  const notesToFill: Array<{ id: string; markdown: string }> = [];

  // === 3. Create every tree note, in depth-first order =================
  doc.transact(() => {
    const addNote = (node: LayoutedNode) => {
      const id = doc.addBlock(
        'affine:note',
        {
          xywh: new Bound(node.x, node.y, node.w, node.h).serialize(),
          displayMode: NoteDisplayMode.EdgelessOnly,
          background: backgroundFor(node.kind),
        },
        doc.root!.id
      );
      node.id = id;
      treeNotePositions.push(new Bound(node.x, node.y, node.w, node.h));
      if (node.kind === 'finding' && node.item) {
        findingNoteByKey.set(itemKey(node.item), id);
      }
      if (node.kind === 'citation' && node.citationToken) {
        citationNoteByToken.set(node.citationToken, id);
      }
      notesToFill.push({ id, markdown: formatNoteMarkdown(node) });
      for (const child of node.children) addNote(child);
    };
    if (layout) addNote(layout);
  });

  // === 4. Fill each note with its markdown (outside transact) ==========
  await Promise.all(
    notesToFill.map(({ id, markdown }) =>
      insertFromMarkdown(host, markdown, doc, id, 0).catch(err => {
        console.warn('[explore-edgeless] failed to fill note:', err);
        return [];
      })
    )
  );

  // === 5. Connectors ===================================================
  // 5a. Tree connectors (root → section → finding, finding → citation
  //     when the citation is already the finding's child in our layout).
  // 5b. Extra finding → citation connectors for shared citations (a
  //     citation placed as Finding-A's child may also be cited by
  //     Finding-B; draw a second connector to reflect the sharing).
  doc.transact(() => {
    if (layout) addTreeConnectors(crud, layout);

    for (const section of tree.sections) {
      if (section.kind !== 'findings') continue;
      for (const item of section.items) {
        const findingId = findingNoteByKey.get(itemKey(item));
        if (!findingId) continue;
        for (const token of item.citations) {
          const citationId = citationNoteByToken.get(token);
          if (!citationId) continue;
          // The layout already attached this citation to its OWNER
          // finding (the first finding in the list that cited it).
          // For every OTHER finding that cites the same token, add an
          // extra connector to show the sharing.
          if (isDirectChild(layout, findingId, citationId)) continue;
          crud.addElement(
            CanvasElementType.CONNECTOR,
            connectorProps(findingId, citationId)
          );
        }
      }
    }
  });

  // === 5c. Cross-turn connector — link this turn's root back to the
  //        prior turn's root so the whole session reads as one graph,
  //        not a stack of disconnected subtrees.
  if (layout?.id) {
    const priorSession = sessionRootByDoc.get(doc as object);
    if (priorSession && priorSession.rootId !== layout.id) {
      doc.transact(() => {
        try {
          crud.addElement(
            CanvasElementType.CONNECTOR,
            connectorProps(priorSession.rootId, layout.id)
          );
        } catch (err) {
          console.warn(
            '[explore-edgeless] cross-turn connector failed:',
            err
          );
        }
      });
    }
    sessionRootByDoc.set(doc as object, {
      rootId: layout.id,
      turnCount: (priorSession?.turnCount ?? 0) + 1,
    });
  }

  edgelessCopilot?.hideCopilotPanel?.();
  aiPanel?.hide?.();

  // === 6. Viewport fit + remember region for next run ==================
  const allBounds: Bound[] = [];
  if (tldrNoteId) {
    const m = doc.getBlock(tldrNoteId)?.model as { xywh?: string } | undefined;
    if (m?.xywh) allBounds.push(Bound.deserialize(m.xywh));
  }
  allBounds.push(...treeNotePositions);
  const combined = combineBounds(allBounds);
  if (combined) {
    lastRegionByDoc.set(doc as object, combined);
  }
  if (combined && layout) {
    setTimeout(() => {
      gfx.viewport.setViewportByBound(combined, [40, 40, 40, 40], true);
      if (layout.id) {
        gfx.selection.set({ elements: [layout.id], editing: false });
      }
    });
  }

  host.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
    control: 'ai',
    page: 'whiteboard editor',
    module: 'toolbar',
    segment: 'toolbar',
    type: 'explore-mindmap',
  });
}

// --- Layout computation --------------------------------------------------

/**
 * Compute deterministic positions for every tree node using a two-pass
 * approach (bottom-up `subtreeHeight`, then top-down placement).
 *
 * Citations are attached as children of the FIRST finding that cites
 * them, placed in column 3 beside that finding. A citation shared
 * across findings gets placed once; additional connectors are drawn
 * later by the caller.
 */
function computeTreeLayout(
  tree: ExploreTree,
  originX: number,
  originY: number
): LayoutedNode | null {
  const sections = tree.sections.filter(
    s => s.kind !== 'sources' && s.items.length > 0
  );
  if (sections.length === 0) return null;

  const placedCitations = new Set<string>();

  const sectionNodes: LayoutedNode[] = sections.map(section => {
    // Keep the signal-to-noise ratio high: drop narrative summary
    // sentences and cap each section at MAX_ITEMS_PER_SECTION cards.
    const filteredItems = section.items
      .filter(isMeaningfulItem)
      .slice(0, MAX_ITEMS_PER_SECTION);
    const itemNodes: LayoutedNode[] = filteredItems.map(item => {
      // For findings (kind='findings'), attach citation children. For
      // other section kinds (papers, questions, nextSteps) skip
      // citations — they would already be their own references.
      const citationKids: LayoutedNode[] =
        section.kind === 'findings'
          ? (item.citations ?? [])
              .filter(token => {
                if (placedCitations.has(token)) return false;
                placedCitations.add(token);
                return true;
              })
              .map(token => {
                const meta = tree.allCitations.get(token);
                return {
                  id: '',
                  kind: 'citation' as NodeKind,
                  text: meta?.label ?? token,
                  x: 0,
                  y: 0,
                  w: CITATION_WIDTH,
                  h: CITATION_HEIGHT,
                  subtreeHeight: CITATION_HEIGHT,
                  children: [],
                  citationToken: token,
                } satisfies LayoutedNode;
              })
          : [];
      const citationsHeight =
        citationKids.length === 0
          ? 0
          : citationKids.reduce((a, c) => a + c.subtreeHeight, 0) +
            (citationKids.length - 1) * CITATION_V_GAP;
      return {
        id: '',
        kind: 'finding' as NodeKind,
        text: formatFindingText(item),
        x: 0,
        y: 0,
        w: FINDING_WIDTH,
        h: FINDING_HEIGHT,
        subtreeHeight: Math.max(FINDING_HEIGHT, citationsHeight),
        children: citationKids,
        item,
      };
    });
    const itemsHeight =
      itemNodes.length === 0
        ? 0
        : itemNodes.reduce((a, f) => a + f.subtreeHeight, 0) +
          (itemNodes.length - 1) * SIBLING_V_GAP;
    return {
      id: '',
      kind: 'section' as NodeKind,
      text: `${iconFor(section.kind)} ${trim(section.title, MAX_NODE_TEXT_SECTION)}`,
      x: 0,
      y: 0,
      w: SECTION_WIDTH,
      h: SECTION_HEIGHT,
      subtreeHeight: Math.max(SECTION_HEIGHT, itemsHeight),
      children: itemNodes,
      sectionKind: section.kind,
    };
  });

  const sectionsHeight =
    sectionNodes.reduce((a, s) => a + s.subtreeHeight, 0) +
    (sectionNodes.length - 1) * SIBLING_V_GAP;

  const root: LayoutedNode = {
    id: '',
    kind: 'root',
    text: trim(tree.query || 'Research', MAX_NODE_TEXT_ROOT),
    x: originX,
    y: originY,
    w: ROOT_WIDTH,
    h: ROOT_HEIGHT,
    subtreeHeight: Math.max(ROOT_HEIGHT, sectionsHeight),
    children: sectionNodes,
  };

  // Place root centred vertically within its full subtree.
  root.y = originY + (root.subtreeHeight - ROOT_HEIGHT) / 2;

  // Place sections in column 1 and their subtrees.
  const sectionsX = root.x + root.w + LEVEL_H_GAP;
  let sectionCurrentTop = originY;
  for (const section of sectionNodes) {
    section.x = sectionsX;
    section.y =
      sectionCurrentTop + (section.subtreeHeight - SECTION_HEIGHT) / 2;

    // Findings in column 2.
    const findingsX = section.x + section.w + LEVEL_H_GAP;
    let findingCurrentTop = sectionCurrentTop;
    for (const finding of section.children) {
      finding.x = findingsX;
      finding.y =
        findingCurrentTop +
        (finding.subtreeHeight - FINDING_HEIGHT) / 2;

      // Citations in column 3, stacked directly to the right of finding.
      const citationsX = finding.x + finding.w + LEVEL_H_GAP;
      let citationCurrentTop = findingCurrentTop;
      for (const citation of finding.children) {
        citation.x = citationsX;
        citation.y = citationCurrentTop;
        citationCurrentTop += citation.h + CITATION_V_GAP;
      }

      findingCurrentTop += finding.subtreeHeight + SIBLING_V_GAP;
    }

    sectionCurrentTop += section.subtreeHeight + SIBLING_V_GAP;
  }

  return root;
}

function isDirectChild(
  root: LayoutedNode | null,
  parentId: string,
  childId: string
): boolean {
  if (!root) return false;
  const walk = (node: LayoutedNode): boolean => {
    if (node.id === parentId) {
      return node.children.some(c => c.id === childId);
    }
    return node.children.some(walk);
  };
  return walk(root);
}

// --- Connectors ----------------------------------------------------------

type CrudLike = {
  addElement: <T extends Record<string, unknown>>(
    type: string,
    props: T
  ) => string | undefined;
};

/**
 * Build a fully-specified connector prop bag. Passing these explicitly
 * overrides any cached `EditPropsStore` values (a prior user action that
 * set a transparent stroke or zero width would otherwise silently
 * obscure every connector we create — that is the "connectors aren't
 * rendering" bug the user was hitting).
 */
function connectorProps(
  fromId: string,
  toId: string,
  mode: ConnectorMode = ConnectorMode.Curve
): Record<string, unknown> {
  return {
    mode,
    controllers: [],
    source: { id: fromId },
    target: { id: toId },
    stroke: DefaultTheme.connectorColor,
    strokeWidth: CONNECTOR_STROKE_WIDTH,
    strokeStyle: CONNECTOR_STROKE_STYLE,
    frontEndpointStyle: 'None',
    rearEndpointStyle: 'Arrow',
  };
}

function addTreeConnectors(crud: CrudLike, root: LayoutedNode): void {
  const connect = (from: string, to: string) => {
    crud.addElement(CanvasElementType.CONNECTOR, connectorProps(from, to));
  };
  const walk = (node: LayoutedNode) => {
    for (const child of node.children) {
      if (node.id && child.id) connect(node.id, child.id);
      walk(child);
    }
  };
  walk(root);
}

// --- Insertion origin ----------------------------------------------------

function computeInsertionOrigin(
  host: EditorHost,
  _surface: ReturnType<typeof getSurfaceBlock>,
  edgelessCopilot: ReturnType<typeof getEdgelessCopilotWidget> | null,
  width: number,
  height: number
): { originX: number; originY: number } {
  const gfx = host.std.get(GfxControllerIdentifier);

  const prior = lastRegionByDoc.get(host.doc as object);
  if (prior) {
    return { originX: prior.x, originY: prior.y + prior.h + RUN_STACK_GAP };
  }

  try {
    const selected = gfx.selection.selectedElements;
    if (
      edgelessCopilot &&
      (selected.length > 0 || edgelessCopilot.selectionModelRect)
    ) {
      const bounds = edgelessCopilot.determineInsertionBounds(width, height);
      return { originX: bounds.x, originY: bounds.y };
    }
  } catch {
    /* fall through */
  }

  const { centerX, centerY } = gfx.viewport;
  return {
    originX: centerX - width / 2,
    originY: centerY - height,
  };
}

// --- Text & style helpers ------------------------------------------------

function formatFindingText(item: ExploreItem): string {
  const grade = item.grade ? `${item.grade} ` : '';
  const label = item.shortLabel || item.text;
  return trim(`${grade}${label}`, MAX_NODE_TEXT_FINDING);
}

/**
 * Drop items that aren't real findings or action items — narrative
 * summary sentences ("These findings provide a comprehensive view…")
 * add noise without helping the reader navigate. Heuristics:
 *   - must have meaningful content (>12 chars)
 *   - not a generic summary opener
 */
function isMeaningfulItem(item: ExploreItem): boolean {
  const body = (item.shortLabel || item.text || '').trim();
  if (body.length < 12) return false;
  const lower = body.toLowerCase();
  const noiseOpeners = [
    'these findings',
    'in summary',
    'in conclusion',
    'overall,',
    'overall ',
    'as a whole',
    'to summarise',
    'to summarize',
  ];
  if (noiseOpeners.some(p => lower.startsWith(p))) return false;
  return true;
}

function formatNoteMarkdown(node: LayoutedNode): string {
  if (node.kind === 'finding') return node.text;
  if (node.kind === 'citation') {
    // Citations render as bold label for a compact chip.
    return `**${node.text}**`;
  }
  return `**${node.text}**`;
}

function trim(text: string, max: number): string {
  const t = (text ?? '').trim();
  if (!t) return '';
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

function iconFor(kind: SectionKind): string {
  switch (kind) {
    case 'findings':
      return '🔬';
    case 'papers':
      return '📄';
    case 'consistency':
      return '⚖️';
    case 'questions':
      return '❓';
    case 'nextSteps':
      return '🚀';
    default:
      return '📌';
  }
}

function backgroundFor(kind: NodeKind): Color {
  // Depth-based palette; keeps the tree scannable at a glance.
  switch (kind) {
    case 'root':
      return NOTE_BG.Blue;
    case 'section':
      return NOTE_BG.Teal;
    case 'finding':
      return NOTE_BG.White;
    case 'citation':
      return NOTE_BG.Yellow;
    default:
      return NOTE_BG.White;
  }
}

function itemKey(item: ExploreItem): string {
  return item.text;
}

function combineBounds(bounds: Bound[]): Bound | null {
  if (!bounds.length) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const b of bounds) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.w);
    maxY = Math.max(maxY, b.y + b.h);
  }
  return new Bound(minX, minY, maxX - minX, maxY - minY);
}
